import { modelsApi } from '@/api/models.api';
import { projectsApi } from '@/api/projects.api';
import type { ModelType, ProjectOverviewModel, UpdateProjectDto } from '@/types/project.types';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

export const useProjectDetail = (projectId: string) => {
  const [project, setProject] = useState<ProjectOverviewModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProject = useCallback(async () => {
    setError(null);

    try {
      const data = await projectsApi.getOverview(projectId);
      setProject(data);
    } catch (_err) {
      const message = 'Failed to load project';
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const updateProject = useCallback(
    async (updates: UpdateProjectDto) => {
      if (!project) return;

      try {
        await projectsApi.update(projectId, updates);
        setProject({ ...project, ...updates });
        toast.success('Projekt aktualizován');
      } catch (err) {
        toast.error('Nepodařilo se aktualizovat projekt');
        throw err;
      }
    },
    [project, projectId]
  );

  const uploadModel = useCallback(
    async (file: File, modelType: ModelType, onProgress?: (progress: number, currentStep: string) => void) => {
      try {
        const modelId = await modelsApi.uploadWithProgress(
          projectId,
          file,
          modelType,
          (progress, currentStep) => {
            if (onProgress) {
              onProgress(progress, currentStep);
            }
          },
          (error) => {
            console.error('Upload error:', error);
          }
        );
        toast.success(`${modelType} model uploaded`);
        await fetchProject(); // Refresh project data
        return { uid: modelId };
      } catch (err) {
        toast.error(`Failed to upload ${modelType} model`);
        throw err;
      }
    },
    [projectId, fetchProject]
  );

  const deleteModel = useCallback(
    async (modelId: string) => {
      try {
        await modelsApi.delete(modelId);
        toast.success('Model deleted');
        await fetchProject(); // Refresh
      } catch (err) {
        toast.error('Failed to delete model');
        throw err;
      }
    },
    [fetchProject]
  );

  return {
    project,
    isLoading,
    error,
    refetch: fetchProject,
    updateProject,
    uploadModel,
    deleteModel,
  };
};
